/**
 * useFocusTrap — traps keyboard focus inside a modal/dialog container.
 *
 * When `active` is true:
 *  - Focuses the first focusable element inside `ref`
 *  - Tab / Shift+Tab cycle only within `ref`
 *  - Focus is restored to the previously focused element on deactivation
 */
import { useEffect, useRef } from "react";

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function useFocusTrap<T extends HTMLElement>(active: boolean) {
  const ref = useRef<T>(null);
  const previouslyFocused = useRef<Element | null>(null);

  useEffect(() => {
    if (!active) return;

    // Save currently focused element
    previouslyFocused.current = document.activeElement;

    const container = ref.current;
    if (!container) return;

    // Focus first focusable child
    const focusable = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (focusable.length > 0) focusable[0].focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const focusableNow = Array.from(
        container.querySelectorAll<HTMLElement>(FOCUSABLE)
      );
      if (focusableNow.length === 0) return;

      const first = focusableNow[0];
      const last = focusableNow[focusableNow.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    container.addEventListener("keydown", handleKeyDown);

    return () => {
      container.removeEventListener("keydown", handleKeyDown);
      // Restore focus on close
      if (
        previouslyFocused.current &&
        "focus" in previouslyFocused.current
      ) {
        (previouslyFocused.current as HTMLElement).focus();
      }
    };
  }, [active]);

  return ref;
}
